/**
 * Budget Ceiling Tracker — joins board ceiling × forecast rollup
 *
 * Reads forecast values through forecastOverrides so user edits flow through
 * to the ceiling dashboard automatically. Source data lives in forecast2026.js;
 * any user override takes precedence.
 *
 * Output shape (one row per parent dept):
 * {
 *   id, parentDept, owner,
 *   ceiling: { budget, salaries, total },     // from board approval
 *   forecast: { annual, monthly: [12] },      // rolled up from child depts
 *   variance: { abs, pct },                   // forecast - ceiling
 *   status: 'green' | 'yellow' | 'red',       // 100% threshold
 *   children: [{ name, annual, monthly }],    // drill-down detail
 * }
 *
 * Status thresholds (per user — 100% is THE ceiling, no 110% buffer):
 *   green:  forecast <= 95% of ceiling
 *   yellow: 95% < forecast <= 100%
 *   red:    forecast > 100%
 */

import { boardCeiling2026, boardCeiling2026Totals } from './boardCeiling2026';
import { deptHierarchy } from './deptHierarchy';
import { forecast2026, MONTHS } from './forecast2026';
import { getMonthly } from './forecastOverrides';

const ZERO_MONTHS = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
const FY = 2026;

const addMonths = (a, b) => a.map((v, i) => v + (b[i] || 0));

// Read a child dept's monthly array, applying overrides where present.
function monthsForChild(childDept) {
  return Array.from({ length: 12 }, (_, m) => getMonthly(childDept, FY, m) || 0);
}

function annualForChild(childDept) {
  return monthsForChild(childDept).reduce((s, v) => s + v, 0);
}

function statusFor(forecast, ceiling) {
  if (ceiling === 0) return forecast > 0 ? 'red' : 'green';
  const ratio = forecast / ceiling;
  if (ratio > 1.0) return 'red';
  if (ratio > 0.95) return 'yellow';
  return 'green';
}

/**
 * For each parent dept, build a row joining ceiling + forecast rollup.
 */
export function buildCeilingTracker() {
  return boardCeiling2026.map((parent) => {
    const childDeptNames = deptHierarchy[parent.id] || [];

    // Roll up monthly forecast across child depts (override-aware)
    const monthly = childDeptNames.reduce((acc, child) => {
      return addMonths(acc, monthsForChild(child));
    }, [...ZERO_MONTHS]);

    const annual = monthly.reduce((a, b) => a + b, 0);

    const ceiling = parent.total;
    const variance = annual - ceiling;
    const variancePct = ceiling === 0 ? null : (annual / ceiling) * 100;

    const children = childDeptNames.map((name) => ({
      name,
      annual: annualForChild(name),
      monthly: monthsForChild(name),
    }));

    return {
      id: parent.id,
      parentDept: parent.parentDept,
      owner: parent.owner,
      objectives: parent.objectives,
      note: parent.note,
      ceiling: {
        budget: parent.budget,
        salaries: parent.salaries,
        total: parent.total,
      },
      forecast: { annual, monthly },
      variance: { abs: variance, pct: variancePct },
      status: statusFor(annual, ceiling),
      children,
    };
  });
}

/**
 * Company-level totals (one row, all depts summed).
 */
export function buildCompanyTotal() {
  const tracker = buildCeilingTracker();
  const ceilingTotal = boardCeiling2026Totals.total;
  const forecastTotal = tracker.reduce((s, p) => s + p.forecast.annual, 0);
  return {
    parentDept: 'All Company',
    ceiling: boardCeiling2026Totals,
    forecast: { annual: forecastTotal },
    variance: {
      abs: forecastTotal - ceilingTotal,
      pct: (forecastTotal / ceilingTotal) * 100,
    },
    status: statusFor(forecastTotal, ceilingTotal),
    parentCount: tracker.length,
    redCount: tracker.filter((t) => t.status === 'red').length,
    yellowCount: tracker.filter((t) => t.status === 'yellow').length,
  };
}

// Kept for compatibility with components that previously read this.
// Now also exports the full child dept list so consumers can iterate.
export const CHILD_DEPTS = Object.keys(forecast2026);

export { MONTHS };
